class SenshenSort extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced) return;
    this.select = this.querySelector('select');
    if (!this.select) return;
    this.abort = new AbortController();
    const listen = (target, name, handler) => target.addEventListener(name, handler, { signal: this.abort.signal });
    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'senshen-sort__trigger';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-describedby', this.select.getAttribute('aria-describedby') || '');
    this.list = document.createElement('div');
    this.list.id = `${this.select.id}-options`;
    this.list.className = 'senshen-sort__options';
    this.list.setAttribute('role', 'listbox');
    this.label = this.select.labels?.[0]?.textContent.trim() || 'Sort by';
    this.list.setAttribute('aria-label', this.label);
    this.list.hidden = true;
    this.trigger.setAttribute('aria-controls', this.list.id);
    this.options = [...this.select.options].map(option => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'senshen-sort__option';
      button.setAttribute('role', 'option');
      button.tabIndex = -1;
      button.textContent = option.textContent.trim();
      button.dataset.value = option.value;
      button.disabled = option.disabled;
      this.list.append(button);
      listen(button, 'click', () => this.choose(button));
      return button;
    });
    this.append(this.trigger, this.list);
    this.select.hidden = true;
    this.dataset.enhanced = 'true';
    this.sync();
    listen(this.trigger, 'click', () => this.list.hidden ? this.open() : this.close());
    listen(this.trigger, 'keydown', event => {
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        this.open();
        if (event.key === 'Home') this.options[0].focus();
        if (event.key === 'End') this.options.at(-1).focus();
      }
    });
    listen(this.list, 'keydown', event => this.navigate(event));
    listen(this.select, 'change', () => this.sync());
    listen(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(); });
    listen(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) this.close(); });
  }
  disconnectedCallback() {
    this.abort?.abort();
  }
  sync() {
    const chosen = this.select.selectedOptions[0];
    this.trigger.textContent = chosen?.textContent.trim() || '';
    this.trigger.setAttribute('aria-label', `${this.label} ${this.trigger.textContent}`);
    this.options.forEach(option => option.setAttribute('aria-selected', String(option.dataset.value === this.select.value)));
  }
  open() {
    this.sync();
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    (this.options.find(option => option.dataset.value === this.select.value) || this.options[0]).focus();
  }
  close(restore = false) {
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restore) this.trigger.focus();
  }
  choose(option) {
    if (option.disabled) return;
    const changed = this.select.value !== option.dataset.value;
    this.select.value = option.dataset.value;
    this.sync();
    this.close(true);
    if (changed) {
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  navigate(event) {
    const index = this.options.indexOf(document.activeElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close(true);
      return;
    }
    if (event.key === 'Tab') { this.close(); return; }
    let target;
    if (event.key === 'ArrowDown') target = (index + 1) % this.options.length;
    if (event.key === 'ArrowUp') target = (index - 1 + this.options.length) % this.options.length;
    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.options.length - 1;
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && event.key !== ' ') {
      const now = performance.now();
      this.search = now - (this.lastSearch || 0) < 1000 ? (this.search || '') + event.key : event.key;
      this.lastSearch = now;
      target = this.options.findIndex(option => option.textContent.toLowerCase().startsWith(this.search.toLowerCase()));
      if (target < 0) target = undefined;
    }
    if (target !== undefined) {
      event.preventDefault();
      this.options[target].focus();
      this.options[target].scrollIntoView({ block: 'nearest' });
    }
  }
}
if (!customElements.get('senshen-sort')) customElements.define('senshen-sort', SenshenSort);
